import { useState, useEffect } from "react";
import {
  collection, query, where, getDocs, onSnapshot,
  doc, getDoc, addDoc, updateDoc, serverTimestamp,
} from "firebase/firestore";
import { db } from "./firebase";
import { fmt, generateReportHTML } from "./utils";

// ─── TRANSLATIONS ─────────────────────────────────────────────────────────────
const PT = {
  fr: {
    loading: "Chargement...",
    notFound: "Portail introuvable",
    notFoundSub: "Ce lien est invalide ou a expiré. Contactez-nous pour obtenir un nouveau lien.",
    hello: "Bonjour",
    project: "Projet",
    status: { draft:"Brouillon", sent:"Envoyé", revised:"Révisé", approved:"Approuvé", rejected:"Révision demandée", pending:"En attente", inProgress:"En cours", completed:"Terminé" },
    tabs: { estimates:"Soumissions", invoices:"Factures", messages:"Messages" },
    hints: { estimates:"Cliquez sur une soumission pour la consulter et l'approuver.", invoices:"Cliquez sur une facture pour la consulter en détail.", messages:"Écrivez-nous un message ci-dessous — nous vous répondrons rapidement." },
    estimate: { title:"Soumission", number:"N°", date:"Date", validUntil:"Valide jusqu'au", total:"Total (avant taxes)", totalTax:"Total (avec taxes)", tps:"TPS (5%)", tvq:"TVQ (9.975%)", approve:"✓ Approuver", approved:"✓ Approuvée", rejected:"Révision demandée", confirmApprove:"Confirmer l'approbation de cette soumission?", back:"← Retour", section:"Section", subtotal:"Sous-total" },
    messages: { placeholder:"Écrivez votre message...", send:"Envoyer", empty:"Aucun message pour l'instant.", you:"Vous", us:"TAF", sentAt:"Envoyé le" },
    invoice: { title:"Facture" },
    footer: "Propulsé par TAF CRM",
    contact: "Nous contacter",
  },
  en: {
    loading: "Loading...",
    notFound: "Portal not found",
    notFoundSub: "This link is invalid or has expired. Contact us to get a new link.",
    hello: "Hello",
    project: "Project",
    status: { draft:"Draft", sent:"Sent", revised:"Revised", approved:"Approved", rejected:"Changes Requested", pending:"Pending", inProgress:"In Progress", completed:"Completed" },
    tabs: { estimates:"Estimates", invoices:"Invoices", messages:"Messages" },
    hints: { estimates:"Click on an estimate to view and approve it.", invoices:"Click on an invoice to view the details.", messages:"Send us a message below — we'll get back to you shortly." },
    estimate: { title:"Estimate", number:"No.", date:"Date", validUntil:"Valid until", total:"Total (before tax)", totalTax:"Total (with tax)", tps:"GST (5%)", tvq:"QST (9.975%)", approve:"✓ Approve", approved:"✓ Approved", rejected:"Changes Requested", confirmApprove:"Confirm approval of this estimate?", back:"← Back", section:"Section", subtotal:"Subtotal" },
    messages: { placeholder:"Write your message...", send:"Send", empty:"No messages yet.", you:"You", us:"TAF", sentAt:"Sent on" },
    invoice: { title:"Invoice" },
    footer: "Powered by TAF CRM",
    contact: "Contact us",
  },
};

// ─── HELPERS ──────────────────────────────────────────────────────────────────
const fmtDate = (d) => d ? new Date(d+"T12:00:00").toLocaleDateString("fr-CA") : "";

function StatusBadge({ status, t }) {
  const colors = {
    draft:"#f59e0b", sent:"#3b82f6", approved:"#10b981",
    rejected:"#ef4444", pending:"#f59e0b", inProgress:"#3b82f6", completed:"#10b981",
  };
  const color = colors[status] || "#a8a29e";
  return (
    <span style={{
      display:"inline-block", padding:"3px 10px", borderRadius:20,
      fontSize:11, fontWeight:700, letterSpacing:".04em",
      background: color+"22", color,
    }}>
      {t.status[status] || status}
    </span>
  );
}

// ─── ESTIMATE DETAIL VIEW ─────────────────────────────────────────────────────
function EstimateDetail({ estimate, customer, settings, t, isInvoice, onBack, onApprove, onApproveCO, onBulkApproveCOs }) {
  const [confirming, setConfirming] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [approvalError, setApprovalError] = useState(false);
  const [coConfirming, setCoConfirming] = useState(null); // {coId, action: "approve"|"reject"}
  const [coSubmitting, setCoSubmitting] = useState(false);
  const [bulkApproveConfirm, setBulkApproveConfirm] = useState(false);
  const [dpLightbox, setDpLightbox] = useState(null); // { photos:[], idx:number }

  useEffect(() => {
    if (!dpLightbox) return;
    const onKey = (e) => {
      if (e.key === "Escape") setDpLightbox(null);
      if (e.key === "ArrowRight") setDpLightbox(p => p && p.idx < p.photos.length - 1 ? {...p, idx: p.idx + 1} : p);
      if (e.key === "ArrowLeft")  setDpLightbox(p => p && p.idx > 0 ? {...p, idx: p.idx - 1} : p);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [dpLightbox]);

  const sectionTotals = (estimate.sections||[]).filter(sec=>sec.type!=="group").map(sec => ({
    title: sec.title,
    description: sec.description||"",
    items: sec.items||[],
    total: sec.price != null ? sec.price : (sec.items||[]).filter(i=>i.type!=="subheading").reduce((a,i) => a+(i.unitPrice||0)*(i.qty||1), 0),
  }));
  const subtotal = sectionTotals.reduce((a,s) => a+s.total, 0);
  const portalCOs = isInvoice ? (estimate.changeOrders||[]) : [];
  const coApproved = (estimate.changeOrders||[]).filter(co=>co.status==="approved"||co.status==="manual").reduce((a,co)=>a+(co.amount||0),0);
  const grandTotal = subtotal + coApproved;
  const tps = Math.round(grandTotal*0.05*100)/100;
  const tvq = Math.round(grandTotal*0.09975*100)/100;
  const grandTotalTax = grandTotal + tps + tvq;

  const handleApproveCOAction = async () => {
    if (!coConfirming || !onApproveCO) return;
    setCoSubmitting(true);
    await onApproveCO(estimate, coConfirming.coId, coConfirming.action);
    setCoConfirming(null);
    setCoSubmitting(false);
  };

  const handleBulkApproveCOs = async () => {
    if (!onBulkApproveCOs) return;
    setCoSubmitting(true);
    await onBulkApproveCOs(estimate);
    setBulkApproveConfirm(false);
    setCoSubmitting(false);
  };

  const isApproved = estimate.status === "approved";
  const canApprove = !isApproved;

  const handleApprove = async () => {
    setSubmitting(true);
    setApprovalError(false);
    try {
      await onApprove(estimate);
      setConfirming(null);
    } catch (err) {
      console.error('Approval error:', err.message, err);
      setApprovalError(true);
    } finally {
      setSubmitting(false);
    }
  };

  const handlePrint = () => {
    const clientConfig = {
      showLineItems: false, showUnitCost: false, showUnitPrice: false,
      showLineItemTotals: false, showSectionSubtotals: true, showTaxes: true,
      showReferenceMaterials: !isInvoice, showDesignPlans: !isInvoice,
    };
    const html = generateReportHTML(isInvoice ? "Facture" : "Soumission", estimate, customer, settings, clientConfig, null);
    const win = window.open("", "_blank");
    win.document.write(html);
    win.document.close();
    win.onload = () => win.print();
  };

  return (
    <div style={{ maxWidth:680, margin:"0 auto" }}>
      <button onClick={onBack} style={{ background:"none", border:"none", color:"#b91c1c", fontWeight:600, fontSize:13, cursor:"pointer", padding:"0 0 16px", fontFamily:"inherit" }}>
        {t.estimate.back}
      </button>

      {/* Header card */}
      <div style={{ background:"#fff", borderRadius:12, border:"1px solid #e5e3df", marginBottom:16, overflow:"hidden" }}>
        <div style={{ background:"#1a0a0a", padding:"16px 20px", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
          <div style={{ color:"white" }}>
            <div style={{ fontSize:11, letterSpacing:".08em", textTransform:"uppercase", opacity:.6, marginBottom:4 }}>
              {isInvoice ? t.invoice.title : t.estimate.title}
            </div>
            <div style={{ fontSize:18, fontWeight:700 }}>{estimate.title}</div>
          </div>
          <div style={{ textAlign:"right" }}>
            <div style={{ fontSize:11, color:"rgba(255,255,255,.5)", marginBottom:4 }}>{t.estimate.number} {estimate.number}</div>
            <StatusBadge status={estimate.status?.toLowerCase()} t={t}/>
          </div>
        </div>
        <div style={{ padding:"14px 20px", display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, background:"#f8f7f5" }}>
          <div>
            <div style={{ fontSize:10, fontWeight:700, letterSpacing:".08em", textTransform:"uppercase", color:"#a8a29e", marginBottom:3 }}>Client</div>
            <div style={{ fontSize:13, fontWeight:600 }}>{customer?.firstName} {customer?.lastName}</div>
            {customer?.company && <div style={{ fontSize:12, color:"#57534e" }}>{customer.company}</div>}
          </div>
          <div>
            <div style={{ fontSize:10, fontWeight:700, letterSpacing:".08em", textTransform:"uppercase", color:"#a8a29e", marginBottom:3 }}>Date</div>
            <div style={{ fontSize:13 }}>{fmtDate(estimate.createdDate || estimate.validUntil)}</div>
            {estimate.validUntil && <div style={{ fontSize:12, color:"#57534e" }}>{t.estimate.validUntil}: {fmtDate(estimate.validUntil)}</div>}
          </div>
        </div>
      </div>

      {/* Line items — sections only */}
      <div style={{ background:"#fff", borderRadius:12, border:"1px solid #e5e3df", marginBottom:16, overflow:"hidden" }}>
        <table style={{ width:"100%", borderCollapse:"collapse" }}>
          <thead>
            <tr style={{ background:"#f8f7f5" }}>
              <th style={{ padding:"10px 16px", textAlign:"left", fontSize:10, fontWeight:700, letterSpacing:".07em", textTransform:"uppercase", color:"#a8a29e", borderBottom:"2px solid #e5e3df" }}>
                {t.estimate.section}
              </th>
              <th style={{ padding:"10px 16px", textAlign:"right", fontSize:10, fontWeight:700, letterSpacing:".07em", textTransform:"uppercase", color:"#a8a29e", borderBottom:"2px solid #e5e3df" }}>
                Total
              </th>
            </tr>
          </thead>
          <tbody>
            {sectionTotals.map((s,i) => (
              <tr key={i} style={{ borderBottom:"1px solid #f3f4f6" }}>
                <td style={{ padding:"12px 16px", fontSize:14, fontWeight:600 }}>
                  {s.title}
                  {s.description && <div style={{ fontSize:12, fontWeight:400, color:"#57534e", marginTop:3 }}>{s.description}</div>}
                </td>
                <td style={{ padding:"12px 16px", textAlign:"right", fontSize:14, fontWeight:600 }}>{fmt(s.total)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Totals */}
        <div style={{ borderTop:"2px solid #e5e3df", padding:"16px 20px", display:"flex", justifyContent:"flex-end" }}>
          <div style={{ width:280 }}>
            {coApproved!==0 && (
              <>
                <div style={{ display:"flex", justifyContent:"space-between", padding:"4px 0", fontSize:12, color:"#a8a29e" }}>
                  <span>Sous-total (base)</span><span>{fmt(subtotal)}</span>
                </div>
                <div style={{ display:"flex", justifyContent:"space-between", padding:"4px 0", fontSize:12, color:"#d97706", fontWeight:600 }}>
                  <span>Avenants approuvés</span><span>+{fmt(coApproved)}</span>
                </div>
              </>
            )}
            <div style={{ display:"flex", justifyContent:"space-between", padding:"6px 0", fontSize:15, fontWeight:700, color:"#1c1917" }}>
              <span>{t.estimate.total}</span>
              <span>{fmt(grandTotal)}</span>
            </div>
            {isInvoice && (
              <>
                <div style={{ display:"flex", justifyContent:"space-between", padding:"4px 0", fontSize:12, color:"#a8a29e" }}>
                  <span>{t.estimate.tps}{settings?.gst ? `  No: ${settings.gst}` : ""}</span><span>{fmt(tps)}</span>
                </div>
                <div style={{ display:"flex", justifyContent:"space-between", padding:"4px 0", fontSize:12, color:"#a8a29e" }}>
                  <span>{t.estimate.tvq}{settings?.qst ? `  No: ${settings.qst}` : ""}</span><span>{fmt(tvq)}</span>
                </div>
                <div style={{ display:"flex", justifyContent:"space-between", padding:"8px 0 0", fontSize:16, fontWeight:700, color:"#1c1917", borderTop:"1px solid #e5e3df", marginTop:4 }}>
                  <span>{t.estimate.totalTax}</span><span>{fmt(grandTotalTax)}</span>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Payment Schedule (invoices only) */}
      {isInvoice && (estimate.paymentSchedule||[]).length > 0 && (()=>{
        const psSub = grandTotal;
        const psTotal = psSub+Math.round(psSub*0.05*100)/100+Math.round(psSub*0.09975*100)/100;
        const encaisse = (estimate.paymentSchedule||[]).filter(ms=>ms.status==="paid").reduce((a,ms)=>a+(ms.paidAmount||ms.amount||0),0);
        const solde = psTotal-encaisse;
        const todayP = new Date().toISOString().split("T")[0];
        return (
          <div style={{ background:"#fff", borderRadius:12, border:"1px solid #e5e3df", marginBottom:16, overflow:"hidden" }}>
            <div style={{ background:"#f8f7f5", padding:"10px 16px", borderBottom:"1px solid #e5e3df" }}>
              <div style={{ fontSize:10, fontWeight:700, letterSpacing:".08em", textTransform:"uppercase", color:"#a8a29e" }}>Calendrier de paiement / Payment Schedule</div>
            </div>
            <table style={{ width:"100%", borderCollapse:"collapse" }}>
              <thead>
                <tr style={{ background:"#f8f7f5" }}>
                  {["Versement / Payment","Montant / Amount","Échéance / Due","Statut / Status"].map((h,i)=>(
                    <th key={i} style={{ padding:"8px 14px", textAlign:i>0?"right":"left", fontSize:10, fontWeight:700, letterSpacing:".06em", textTransform:"uppercase", color:"#a8a29e", borderBottom:"1px solid #e5e3df" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {estimate.paymentSchedule.map((ms,i)=>{
                  const isOverdue = ms.status==="pending" && ms.dueDate && ms.dueDate < todayP;
                  const dueText = ms.dueDate ? ms.dueDate : ms.dueTrigger==="completion" ? "À la complétion" : ms.dueTrigger==="signing" ? "À la signature" : "—";
                  const statusText = ms.status==="paid" ? `✅ Payé${ms.paidAt?" le "+ms.paidAt:""}` : isOverdue ? "🔴 En retard" : `⏳ ${dueText}`;
                  const statusColor = ms.status==="paid" ? "#16a34a" : isOverdue ? "#991b1b" : "#1d4ed8";
                  return (
                    <tr key={i} style={{ borderBottom:"1px solid #f3f4f6" }}>
                      <td style={{ padding:"10px 14px", fontSize:13, fontWeight:600 }}>{ms.label}</td>
                      <td style={{ padding:"10px 14px", textAlign:"right", fontSize:13, fontWeight:600 }}>{fmt(ms.paidAmount||ms.amount)}</td>
                      <td style={{ padding:"10px 14px", textAlign:"right", fontSize:12, color:"#57534e" }}>{ms.sentAt ? dueText : <span style={{color:"#a8a29e",fontSize:11}}>{t===PT.fr?"À confirmer":"To be confirmed"}</span>}</td>
                      <td style={{ padding:"10px 14px", textAlign:"right", fontSize:12, fontWeight:600, color:statusColor }}>{statusText}</td>
                    </tr>
                  );
                })}
                {(()=>{
                  const coTotal = (estimate.changeOrders||[]).filter(co=>co.status==="approved"||co.status==="manual").reduce((a,co)=>a+(co.amount||0),0);
                  if (coTotal <= 0) return null;
                  const paymentsReceived = (estimate.payments||[]).reduce((a,p)=>a+(p.amount||0),0);
                  const coPaid = paymentsReceived >= grandTotalTax - 0.01;
                  const coLabel = t===PT.fr ? "Avenants approuvés" : "Approved change orders";
                  return (
                    <tr style={{ borderTop:"2px solid #e5e3df", background:"#fffbeb" }}>
                      <td style={{ padding:"10px 14px", fontSize:13, fontStyle:"italic", color:"#92400e" }}>{coLabel}</td>
                      <td style={{ padding:"10px 14px", textAlign:"right", fontSize:13, fontWeight:600, color:"#92400e" }}>{fmt(coTotal)}</td>
                      <td style={{ padding:"10px 14px", textAlign:"right", fontSize:12, color:"#a8a29e" }}>—</td>
                      <td style={{ padding:"10px 14px", textAlign:"right", fontSize:12, fontWeight:600, color:coPaid?"#16a34a":"#d97706" }}>
                        {coPaid ? "✅ Payé" : `⏳ ${t===PT.fr?"En attente":"Pending"}`}
                      </td>
                    </tr>
                  );
                })()}
              </tbody>
            </table>
            <div style={{ borderTop:"1px solid #e5e3df", padding:"12px 16px", display:"flex", justifyContent:"flex-end", gap:24 }}>
              <div style={{ textAlign:"right" }}>
                <div style={{ fontSize:10, color:"#a8a29e", textTransform:"uppercase", letterSpacing:".06em" }}>Encaissé / Received</div>
                <div style={{ fontWeight:700, color:"#16a34a", fontSize:14 }}>{fmt(encaisse)}</div>
              </div>
              <div style={{ textAlign:"right" }}>
                <div style={{ fontSize:10, color:"#a8a29e", textTransform:"uppercase", letterSpacing:".06em" }}>Solde restant / Balance</div>
                <div style={{ fontWeight:700, color:solde>0?"#b91c1c":"#16a34a", fontSize:14 }}>{fmt(solde)}</div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Change Orders (all COs, unified section with status-based styling and bulk approval) */}
      {isInvoice && portalCOs.length > 0 && (()=>{
        const pendingCOs = portalCOs.filter(co => co.status === "pending");
        const hasBulk = pendingCOs.length >= 2;
        const coBorderColors = { approved:"#16a34a", pending:"#d97706", rejected:"#dc2626", manual:"#6b7280" };
        const coBgColors = { approved:"#f0fdf4", pending:"#fffbeb", rejected:"#fef2f2", manual:"#f9fafb" };
        const coStatusColors = { pending:"#d97706", approved:"#16a34a", manual:"#16a34a", rejected:"#991b1b" };
        const coStatusLabels = {
          pending:  t===PT.fr ? "En attente" : "Pending",
          approved: t===PT.fr ? "✓ Approuvé" : "✓ Approved",
          manual:   t===PT.fr ? "✓ Approuvé" : "✓ Approved",
          rejected: t===PT.fr ? "Refusé"     : "Rejected",
        };
        return (
          <div style={{ background:"#fff", borderRadius:12, border:"1px solid #e5e3df", marginBottom:16, overflow:"hidden" }}>
            <div style={{ background:"#f8f7f5", padding:"10px 16px", borderBottom:"1px solid #e5e3df", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
              <div style={{ fontSize:10, fontWeight:700, letterSpacing:".08em", textTransform:"uppercase", color:"#a8a29e" }}>Avenants / Change Orders</div>
              {hasBulk && !bulkApproveConfirm && (
                <button onClick={()=>setBulkApproveConfirm(true)} style={{ fontSize:12, fontWeight:700, color:"#16a34a", background:"#f0fdf4", border:"1px solid #bbf7d0", borderRadius:8, padding:"4px 12px", cursor:"pointer", fontFamily:"inherit" }}>
                  Tout approuver / Approve all
                </button>
              )}
            </div>
            {bulkApproveConfirm && (
              <div style={{ background:"#f0fdf4", borderBottom:"1px solid #bbf7d0", padding:"12px 16px" }}>
                <p style={{ fontSize:13, color:"#166534", marginBottom:10 }}>Approuver tous les avenants en attente? / Approve all pending change orders?</p>
                <div style={{ display:"flex", gap:8 }}>
                  <button onClick={handleBulkApproveCOs} disabled={coSubmitting} style={{ padding:"8px 16px", background:"#10b981", color:"white", border:"none", borderRadius:8, fontSize:13, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>
                    {coSubmitting ? "..." : "Confirmer"}
                  </button>
                  <button onClick={()=>setBulkApproveConfirm(false)} style={{ padding:"8px 16px", background:"#fff", color:"#57534e", border:"1px solid #e5e3df", borderRadius:8, fontSize:13, cursor:"pointer", fontFamily:"inherit" }}>Annuler</button>
                </div>
              </div>
            )}
            {portalCOs.map(co => {
              const isPending = co.status === "pending";
              const isApprovedCO = co.status === "approved" || co.status === "manual";
              const isRejected = co.status === "rejected";
              const borderColor = coBorderColors[co.status] || "#a8a29e";
              const bgColor = coBgColors[co.status] || "#f9fafb";
              const statusColor = coStatusColors[co.status] || "#a8a29e";
              const label = coStatusLabels[co.status] || co.status;
              const amt = co.amount || 0;
              return (
                <div key={co.id} style={{ borderLeft:`3px solid ${borderColor}`, background:bgColor, padding:"14px 16px", borderBottom:"1px solid #e5e3df" }}>
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:12 }}>
                    <div>
                      <div style={{ fontWeight:700, fontSize:14, color:"#1c1917" }}>{co.number}{co.description ? ` — ${co.description}` : ""}</div>
                      <div style={{ fontSize:13, fontWeight:600, color:amt<0?"#991b1b":"#d97706", marginTop:3 }}>{(amt<0?"-":"+")+fmt(Math.abs(amt))}</div>
                    </div>
                    <span style={{ flexShrink:0, fontSize:11, fontWeight:700, padding:"3px 10px", borderRadius:20, background:statusColor+"22", color:statusColor }}>{label}</span>
                  </div>
                  {isPending && coConfirming?.coId !== co.id && (
                    <div style={{ display:"flex", gap:8, marginTop:12 }}>
                      <button onClick={()=>setCoConfirming({coId:co.id,action:"approve"})} style={{ flex:1, padding:"10px", background:"#10b981", color:"white", border:"none", borderRadius:8, fontSize:13, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>✓ Approuver / Approve</button>
                      <button onClick={()=>setCoConfirming({coId:co.id,action:"reject"})} style={{ flex:1, padding:"10px", background:"#fff", color:"#991b1b", border:"1px solid #fecaca", borderRadius:8, fontSize:13, fontWeight:600, cursor:"pointer", fontFamily:"inherit" }}>Refuser / Reject</button>
                    </div>
                  )}
                  {isPending && coConfirming?.coId === co.id && (
                    <div style={{ marginTop:12, background:coConfirming.action==="approve"?"#f0fdf4":"#fff5f5", border:`1px solid ${coConfirming.action==="approve"?"#bbf7d0":"#fecaca"}`, borderRadius:8, padding:14 }}>
                      <p style={{ marginBottom:12, fontSize:13, color:coConfirming.action==="approve"?"#166534":"#991b1b" }}>
                        {coConfirming.action==="approve" ? `Confirmer l'approbation de ${co.number} (+${fmt(co.amount)})?` : `Confirmer le refus de ${co.number}?`}
                      </p>
                      <div style={{ display:"flex", gap:8 }}>
                        <button onClick={handleApproveCOAction} disabled={coSubmitting} style={{ flex:1, padding:"10px", background:coConfirming.action==="approve"?"#10b981":"#ef4444", color:"white", border:"none", borderRadius:8, fontSize:13, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>
                          {coSubmitting ? "..." : coConfirming.action==="approve" ? "✓ Approuver" : "Refuser"}
                        </button>
                        <button onClick={()=>setCoConfirming(null)} style={{ padding:"10px 16px", background:"#fff", color:"#57534e", border:"1px solid #e5e3df", borderRadius:8, fontSize:13, cursor:"pointer", fontFamily:"inherit" }}>Annuler</button>
                      </div>
                    </div>
                  )}
                  {isApprovedCO && <div style={{ marginTop:8, fontSize:12, color:"#16a34a" }}>✓ Approuvé le {co.approvedAt ? new Date(co.approvedAt).toLocaleDateString("fr-CA") : ""}{co.approvedBy==="portal" ? " via le portail" : ""}</div>}
                  {isRejected && <div style={{ marginTop:8, fontSize:12, color:"#991b1b" }}>Refusé le {co.rejectedAt ? new Date(co.rejectedAt).toLocaleDateString("fr-CA") : ""}</div>}
                </div>
              );
            })}
          </div>
        );
      })()}

      {/* Payment History (invoices only) */}
      {isInvoice && Array.isArray(estimate.payments) && estimate.payments.length > 0 && (()=>{
        const paymentsTotal = estimate.payments.reduce((a,p) => a+(p.amount||0), 0);
        const balanceOwing = grandTotalTax - paymentsTotal;
        const hasNotes = estimate.payments.some(p=>p.notes);
        const loc = t===PT.fr ? "fr-CA" : "en-CA";
        const fmtDate = (d) => d ? new Date(d).toLocaleDateString(loc, {day:"2-digit",month:"short",year:"numeric"}) : "—";
        return (
          <div style={{ background:"#fff", borderRadius:12, border:"1px solid #e5e3df", marginBottom:16, overflow:"hidden" }}>
            <div style={{ background:"#f8f7f5", padding:"10px 16px", borderBottom:"1px solid #e5e3df" }}>
              <div style={{ fontSize:10, fontWeight:700, letterSpacing:".08em", textTransform:"uppercase", color:"#a8a29e" }}>
                {t===PT.fr ? "Paiements reçus" : "Payments Received"}
              </div>
            </div>
            <table style={{ width:"100%", borderCollapse:"collapse" }}>
              <thead>
                <tr style={{ background:"#f8f7f5" }}>
                  {[t===PT.fr?"Date":"Date", t===PT.fr?"Montant":"Amount", t===PT.fr?"Méthode":"Method", ...(hasNotes?[t===PT.fr?"Notes":"Notes"]:[])].map((h,i)=>(
                    <th key={i} style={{ padding:"8px 14px", textAlign:i===0?"left":"right", fontSize:10, fontWeight:700, letterSpacing:".06em", textTransform:"uppercase", color:"#a8a29e", borderBottom:"1px solid #e5e3df" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {estimate.payments.map((p,i)=>(
                  <tr key={p.id||i} style={{ borderBottom:"1px solid #f3f4f6" }}>
                    <td style={{ padding:"10px 14px", fontSize:13 }}>{fmtDate(p.date)}</td>
                    <td style={{ padding:"10px 14px", textAlign:"right", fontSize:13, fontWeight:600 }}>{fmt(p.amount)}</td>
                    <td style={{ padding:"10px 14px", textAlign:"right", fontSize:13, color:"#57534e" }}>{p.method||"—"}</td>
                    {hasNotes&&<td style={{ padding:"10px 14px", textAlign:"right", fontSize:12, color:"#a8a29e" }}>{p.notes||""}</td>}
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{ borderTop:"1px solid #e5e3df", padding:"12px 16px", display:"flex", justifyContent:"flex-end", gap:24 }}>
              <div style={{ textAlign:"right" }}>
                <div style={{ fontSize:10, color:"#a8a29e", textTransform:"uppercase", letterSpacing:".06em" }}>{t===PT.fr?"Total reçu":"Total received"}</div>
                <div style={{ fontWeight:700, color:"#16a34a", fontSize:14 }}>{fmt(paymentsTotal)}</div>
              </div>
              <div style={{ textAlign:"right" }}>
                <div style={{ fontSize:10, color:"#a8a29e", textTransform:"uppercase", letterSpacing:".06em" }}>{t===PT.fr?"Solde restant":"Balance owing"}</div>
                <div style={{ fontWeight:700, color:balanceOwing<=0?"#16a34a":"#d97706", fontSize:14 }}>{fmt(Math.max(0,balanceOwing))}</div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Reference Materials */}
      {!isInvoice && Array.isArray(estimate.referencePhotos) && estimate.referencePhotos.length > 0 && (
        <div style={{ background:"#fff", borderRadius:12, border:"1px solid #e5e3df", marginBottom:16, overflow:"hidden" }}>
          <div style={{ background:"#f8f7f5", padding:"10px 16px", borderBottom:"1px solid #e5e3df" }}>
            <div style={{ fontSize:10, fontWeight:700, letterSpacing:".08em", textTransform:"uppercase", color:"#a8a29e" }}>
              {t===PT.fr ? "Matériaux de référence" : "Reference Materials"}
            </div>
          </div>
          <div style={{ padding:"16px", display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(130px,1fr))", gap:12 }}>
            {estimate.referencePhotos.map((photo,i) => (
              <div key={i} style={{ textAlign:"center" }}>
                <img src={photo.photoURL} alt={photo.name} style={{ width:"100%", aspectRatio:"4/3", objectFit:"cover", borderRadius:8, marginBottom:6, display:"block" }}/>
                <div style={{ fontSize:12, fontWeight:600, color:"#1c1917" }}>{photo.name}</div>
                {photo.caption && <div style={{ fontSize:11, color:"#57534e", marginTop:2, lineHeight:1.4 }}>{photo.caption}</div>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Design Plans & Renders */}
      {!isInvoice && Array.isArray(estimate.designPlans) && estimate.designPlans.length > 0 && (
        <div style={{ background:"#fff", borderRadius:12, border:"1px solid #e5e3df", marginBottom:16, overflow:"hidden" }}>
          <div style={{ background:"#f8f7f5", padding:"10px 16px", borderBottom:"1px solid #e5e3df" }}>
            <div style={{ fontSize:10, fontWeight:700, letterSpacing:".08em", textTransform:"uppercase", color:"#a8a29e" }}>
              {t===PT.fr ? "Plans & Rendus" : "Design Plans"}
            </div>
          </div>
          <div style={{ padding:"16px", display:"flex", flexDirection:"column", gap:16 }}>
            {estimate.designPlans.map((plan, i) => (
              <div key={plan.id||i} style={{ borderRadius:8, overflow:"hidden", border:"1px solid #e5e3df" }}>
                <img
                  src={plan.photoURL} alt={plan.name||""}
                  onClick={() => setDpLightbox({ photos: estimate.designPlans, idx: i })}
                  style={{ width:"100%", display:"block", maxHeight:480, objectFit:"contain", background:"#f8f7f5", cursor:"zoom-in" }}
                />
                {(plan.name || plan.caption) && (
                  <div style={{ padding:"8px 12px", borderTop:"1px solid #e5e3df", background:"#fafaf9" }}>
                    {plan.name && <div style={{ fontSize:12, fontWeight:600, color:"#1c1917" }}>{plan.name}</div>}
                    {plan.caption && <div style={{ fontSize:11, color:"#57534e", marginTop:2, lineHeight:1.4 }}>{plan.caption}</div>}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Terms & Conditions */}
      {settings?.terms && (
        <div style={{ background:"#fff", borderRadius:12, border:"1px solid #e5e3df", marginBottom:16, overflow:"hidden" }}>
          <div style={{ background:"#f8f7f5", padding:"10px 16px", borderBottom:"1px solid #e5e3df" }}>
            <div style={{ fontSize:10, fontWeight:700, letterSpacing:".08em", textTransform:"uppercase", color:"#a8a29e" }}>Modalités et Conditions</div>
          </div>
          <div style={{ padding:"14px 16px", fontSize:12, color:"#57534e", lineHeight:1.8, whiteSpace:"pre-line" }}>
            {settings.terms}
          </div>
        </div>
      )}

      {/* Action buttons */}
      {!isInvoice && (
        <div style={{ marginBottom:16 }}>
          {isApproved && (
            <div style={{ background:"#f0fdf4", border:"1px solid #bbf7d0", borderRadius:12, padding:"14px 20px", textAlign:"center", color:"#16a34a", fontWeight:700, fontSize:15 }}>
              ✓ {t.estimate.approved}
            </div>
          )}
          {!isApproved && canApprove && confirming === null && (
            <button
              onClick={() => setConfirming("approve")}
              style={{ width:"100%", padding:"14px", background:"#10b981", color:"white", border:"none", borderRadius:10, fontSize:15, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}
            >
              {t.estimate.approve}
            </button>
          )}
          {confirming === "approve" && (
            <div style={{ background:"#f0fdf4", border:"1px solid #bbf7d0", borderRadius:12, padding:"20px" }}>
              <p style={{ marginBottom:16, fontSize:14, color:"#166534" }}>{t.estimate.confirmApprove}</p>
              <div style={{ display:"flex", gap:10 }}>
                <button onClick={handleApprove} disabled={submitting} style={{ flex:1, padding:"12px", background:"#10b981", color:"white", border:"none", borderRadius:8, fontSize:14, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>
                  {submitting ? "..." : t.estimate.approve}
                </button>
                <button onClick={() => setConfirming(null)} style={{ padding:"12px 20px", background:"#fff", color:"#57534e", border:"1px solid #e5e3df", borderRadius:8, fontSize:14, cursor:"pointer", fontFamily:"inherit" }}>
                  ✕
                </button>
              </div>
              {approvalError && <p style={{color:'#dc2626', marginTop:8, fontSize:14}}>Une erreur est survenue. Veuillez réessayer.</p>}
            </div>
          )}
        </div>
      )}

      {/* Print button */}
      <div style={{ marginBottom:16 }}>
        <button
          onClick={handlePrint}
          style={{ width:"100%", padding:"12px", background:"#fff", color:"#57534e", border:"1px solid #e5e3df", borderRadius:10, fontSize:14, fontWeight:600, cursor:"pointer", fontFamily:"inherit", display:"flex", alignItems:"center", justifyContent:"center", gap:8 }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect x="6" y="14" width="12" height="8"/></svg>
          Imprimer / Print
        </button>
      </div>

      {/* Design Plans lightbox */}
      {dpLightbox && (
        <div onClick={() => setDpLightbox(null)} style={{ position:"fixed", inset:0, background:"rgba(0,0,0,.92)", zIndex:9000, display:"flex", alignItems:"center", justifyContent:"center", cursor:"zoom-out" }}>
          <button onClick={e => { e.stopPropagation(); setDpLightbox(null); }} style={{ position:"absolute", top:16, right:16, background:"rgba(255,255,255,.15)", border:"none", color:"white", width:36, height:36, borderRadius:"50%", fontSize:18, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", lineHeight:1 }}>✕</button>
          {dpLightbox.idx > 0 && (
            <button onClick={e => { e.stopPropagation(); setDpLightbox(p => ({...p, idx: p.idx - 1})); }} style={{ position:"absolute", left:16, background:"rgba(255,255,255,.15)", border:"none", color:"white", width:44, height:44, borderRadius:"50%", fontSize:24, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center" }}>‹</button>
          )}
          <img
            src={dpLightbox.photos[dpLightbox.idx].photoURL}
            alt={dpLightbox.photos[dpLightbox.idx].name || ""}
            onClick={e => e.stopPropagation()}
            style={{ maxWidth:"95vw", maxHeight:"90vh", objectFit:"contain", borderRadius:4, display:"block" }}
          />
          {dpLightbox.idx < dpLightbox.photos.length - 1 && (
            <button onClick={e => { e.stopPropagation(); setDpLightbox(p => ({...p, idx: p.idx + 1})); }} style={{ position:"absolute", right:16, background:"rgba(255,255,255,.15)", border:"none", color:"white", width:44, height:44, borderRadius:"50%", fontSize:24, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center" }}>›</button>
          )}
          {dpLightbox.photos[dpLightbox.idx].caption && (
            <div style={{ position:"absolute", bottom:24, left:"50%", transform:"translateX(-50%)", background:"rgba(0,0,0,.6)", color:"white", padding:"6px 16px", borderRadius:20, fontSize:13, maxWidth:"80vw", textAlign:"center", backdropFilter:"blur(4px)" }}>
              {dpLightbox.photos[dpLightbox.idx].caption}
            </div>
          )}
          {dpLightbox.photos.length > 1 && (
            <div style={{ position:"absolute", bottom:dpLightbox.photos[dpLightbox.idx].caption ? 68 : 24, left:"50%", transform:"translateX(-50%)", display:"flex", gap:6 }}>
              {dpLightbox.photos.map((_,i) => (
                <div key={i} onClick={e => { e.stopPropagation(); setDpLightbox(p => ({...p, idx: i})); }} style={{ width:8, height:8, borderRadius:"50%", background:i===dpLightbox.idx?"white":"rgba(255,255,255,.4)", cursor:"pointer" }}/>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── MESSAGES TAB ─────────────────────────────────────────────────────────────
function MessagesTab({ comms, customerId, t, onSend }) {
  const [msg, setMsg] = useState("");
  const [sending, setSending] = useState(false);

  const messages = comms
    .filter(c => c.customerId === customerId && c.type === "portal")
    .sort((a,b) => (a.date||"").localeCompare(b.date||"") || (a.createdAt?.seconds||0)-(b.createdAt?.seconds||0));

  const send = async () => {
    if (!msg.trim()) return;
    setSending(true);
    await onSend(msg.trim());
    setMsg("");
    setSending(false);
  };

  return (
    <div style={{ maxWidth:680, margin:"0 auto" }}>
      <div style={{ fontSize:12,color:"#a8a29e",fontStyle:"italic",marginBottom:12 }}>ℹ️ {t.hints.messages}</div>
      {/* Message thread */}
      <div style={{ marginBottom:16, display:"flex", flexDirection:"column", gap:10, minHeight:200 }}>
        {messages.length === 0 && (
          <div style={{ textAlign:"center", color:"#a8a29e", fontSize:13, padding:"40px 0" }}>{t.messages.empty}</div>
        )}
        {messages.map(c => {
          const isClient = c.direction === "inbound";
          return (
            <div key={c.id} style={{ display:"flex", flexDirection:"column", alignItems: isClient ? "flex-end" : "flex-start" }}>
              <div style={{
                maxWidth:"80%", padding:"10px 14px", borderRadius: isClient ? "12px 12px 2px 12px" : "12px 12px 12px 2px",
                background: isClient ? "#b91c1c" : "#f3f4f6",
                color: isClient ? "white" : "#1c1917",
                fontSize:13, lineHeight:1.5,
              }}>
                {c.body||c.subject}
              </div>
              <div style={{ fontSize:10, color:"#a8a29e", marginTop:3, padding:"0 4px" }}>
                {isClient ? t.messages.you : (c.responsible || t.messages.us)} · {c.date}
              </div>
            </div>
          );
        })}
      </div>

      {/* Input */}
      <div style={{ display:"flex", gap:10, alignItems:"flex-end" }}>
        <textarea
          value={msg}
          onChange={e => setMsg(e.target.value)}
          onKeyDown={e => { if(e.key==="Enter"&&!e.shiftKey){ e.preventDefault(); send(); }}}
          placeholder={t.messages.placeholder}
          rows={3}
          style={{ flex:1, padding:"12px", border:"1px solid #e5e3df", borderRadius:10, fontSize:13, fontFamily:"inherit", resize:"none", outline:"none" }}
        />
        <button
          onClick={send}
          disabled={sending||!msg.trim()}
          style={{ padding:"12px 20px", background:"#b91c1c", color:"white", border:"none", borderRadius:10, fontSize:13, fontWeight:700, cursor:"pointer", fontFamily:"inherit", opacity:msg.trim()?1:.5 }}
        >
          {sending ? "..." : t.messages.send}
        </button>
      </div>
    </div>
  );
}

// ─── MAIN PORTAL ──────────────────────────────────────────────────────────────
export default function Portal() {
  const [lang, setLang] = useState("fr");
  const t = PT[lang];

  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [customer, setCustomer] = useState(null);
  const [estimates, setEstimates] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [comms, setComms] = useState([]);
  const [settings, setSettings] = useState(null);
  const [tab, setTab] = useState("estimates");
  const [selectedId, setSelectedId] = useState(null);
  const [selectedType, setSelectedType] = useState("estimates");
  // Derive from live data so it stays in sync with onSnapshot updates
  const selectedEstimate = selectedType === "invoices"
    ? invoices.find(e => e.id === selectedId) || null
    : estimates.find(e => e.id === selectedId) || null;

  // Extract token from URL: /portal/TOKEN or ?token=TOKEN
  const token = window.location.pathname.split("/portal/")[1]?.split("/")[0]
    || new URLSearchParams(window.location.search).get("token");

  useEffect(() => {
    if (!token) { setNotFound(true); setLoading(false); return; }

    let unsubEstimates = null;
    let unsubInvoices = null;
    let unsubComms = null;

    const init = async () => {
      try {
        // One-time lookup: find customer by portalToken
        const q = query(collection(db, "customers"), where("portalToken", "==", token));
        const snap = await getDocs(q);
        if (snap.empty) { setNotFound(true); setLoading(false); return; }

        const custDoc = snap.docs[0];
        const cust = { id: custDoc.id, ...custDoc.data() };
        setCustomer(cust);

        // Fire-and-forget: track portal last visit (non-critical)
        updateDoc(doc(db, "customers", cust.id), { portalLastVisit: serverTimestamp() }).catch(() => {});

        // Real-time listener: estimates for this customer
        const estQ = query(collection(db, "estimates"), where("customerId", "==", cust.id));
        unsubEstimates = onSnapshot(estQ, (estSnap) => {
          setEstimates(estSnap.docs.map(d => ({ id: d.id, ...d.data() })));
          setLoading(false);
        });

        // Real-time listener: published invoices for this customer
        const invQ = query(collection(db, "invoices"), where("customerId", "==", cust.id));
        unsubInvoices = onSnapshot(invQ, (invSnap) => {
          setInvoices(invSnap.docs.map(d => ({ id: d.id, ...d.data() })).filter(i => i.isPublished));
        });

        // Real-time listener: communications for this customer
        const commQ = query(collection(db, "communications"), where("customerId", "==", cust.id));
        unsubComms = onSnapshot(commQ, (commSnap) => {
          const commsData = commSnap.docs.map(d => ({ id: d.id, ...d.data() }));
          commsData.sort((a,b) => (a.createdAt?.seconds||0) - (b.createdAt?.seconds||0));
          setComms(commsData);
        });

        // One-time load: settings (branding rarely changes mid-session)
        const settingsDoc = await getDoc(doc(db, "settings", "company"));
        if (settingsDoc.exists()) setSettings(settingsDoc.data());

      } catch (err) {
        console.error("Portal load error:", err);
        setNotFound(true);
        setLoading(false);
      }
    };

    init();

    return () => {
      if (unsubEstimates) unsubEstimates();
      if (unsubInvoices) unsubInvoices();
      if (unsubComms) unsubComms();
    };
    // eslint-disable-next-line
  }, [token]);

  const handleApprove = async (estimate) => {
    await updateDoc(doc(db, "estimates", estimate.id), { status: "approved" });
    await addDoc(collection(db, "communications"), {
      customerId: customer.id,
      estimateId: estimate.id,
      type: "portal",
      direction: "inbound",
      subject: `Soumission approuvée: ${estimate.title}`,
      body: `Le client a approuvé la soumission ${estimate.number} via le portail.`,
      date: new Date().toISOString().split("T")[0],
      responsible: `${customer.firstName} ${customer.lastName}`,
      isTask: false,
      taskDone: false,
      createdAt: serverTimestamp(),
      portalApprovedAt: new Date().toISOString(),
    });
    // onSnapshot will update estimates and comms state automatically
  };

  const handleApproveCO = async (invoice, coId, action) => {
    const updatedCOs = (invoice.changeOrders||[]).map(co =>
      co.id === coId
        ? { ...co, status: action==="approve" ? "approved" : "rejected", ...(action==="approve" ? {approvedAt:new Date().toISOString(),approvedBy:"portal"} : {rejectedAt:new Date().toISOString()}) }
        : co
    );
    await updateDoc(doc(db, "invoices", invoice.id), { changeOrders: updatedCOs });
    const co = invoice.changeOrders?.find(c=>c.id===coId);
    await addDoc(collection(db, "communications"), {
      customerId: customer.id,
      invoiceId: invoice.id,
      type: "portal",
      direction: "inbound",
      subject: action==="approve" ? `Avenant approuvé: ${co?.number||coId}` : `Avenant refusé: ${co?.number||coId}`,
      body: action==="approve"
        ? `Le client a approuvé l'avenant ${co?.number||""} (${fmt(co?.amount||0)}) via le portail.`
        : `Le client a refusé l'avenant ${co?.number||""} via le portail.`,
      date: new Date().toISOString().split("T")[0],
      responsible: `${customer.firstName} ${customer.lastName}`,
      isTask: false,
      taskDone: false,
      createdAt: serverTimestamp(),
    });
  };

  const handleBulkApproveCOs = async (invoice) => {
    const now = new Date().toISOString();
    const updatedCOs = (invoice.changeOrders||[]).map(co =>
      co.status === "pending"
        ? { ...co, status:"approved", approvedAt:now, approvedBy:"portal" }
        : co
    );
    await updateDoc(doc(db, "invoices", invoice.id), { changeOrders: updatedCOs });
    const pendingCOs = (invoice.changeOrders||[]).filter(co=>co.status==="pending");
    if (pendingCOs.length > 0) {
      await addDoc(collection(db, "communications"), {
        customerId: customer.id,
        invoiceId: invoice.id,
        type: "portal",
        direction: "inbound",
        subject: `${pendingCOs.length} avenants approuvés`,
        body: `Le client a approuvé ${pendingCOs.length} avenant(s) en attente (${pendingCOs.map(co=>`${co.number} +${fmt(co.amount)}`).join(", ")}) via le portail.`,
        date: new Date().toISOString().split("T")[0],
        responsible: `${customer.firstName} ${customer.lastName}`,
        isTask: false,
        taskDone: false,
        createdAt: serverTimestamp(),
      });
    }
  };

  const handleSendMessage = async (text) => {
    await addDoc(collection(db, "communications"), {
      customerId: customer.id,
      type: "portal",
      direction: "inbound",
      subject: text.slice(0,60),
      body: text,
      date: new Date().toISOString().split("T")[0],
      responsible: `${customer.firstName} ${customer.lastName}`,
      isTask: false,
      taskDone: false,
      createdAt: serverTimestamp(),
    });
    // onSnapshot will update comms state automatically
  };

  // ── Loading ──
  if (loading) return (
    <div style={{ display:"flex",alignItems:"center",justifyContent:"center",height:"100vh",fontFamily:"DM Sans,sans-serif",color:"#a8a29e",fontSize:14,background:"#faf9f7" }}>
      {t.loading}
    </div>
  );

  // ── Not found ──
  if (notFound || !customer) return (
    <div style={{ display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",height:"100vh",fontFamily:"DM Sans,sans-serif",background:"#faf9f7",padding:24,textAlign:"center" }}>
      <div style={{ width:56,height:56,background:"#b91c1c",borderRadius:12,display:"flex",alignItems:"center",justifyContent:"center",color:"white",fontWeight:800,fontSize:18,marginBottom:20 }}>TAF</div>
      <div style={{ fontSize:20,fontWeight:700,color:"#1c1917",marginBottom:8 }}>{t.notFound}</div>
      <div style={{ fontSize:13,color:"#a8a29e",maxWidth:320,lineHeight:1.6 }}>{t.notFoundSub}</div>
      {settings?.phone && <div style={{ marginTop:20,fontSize:13,color:"#b91c1c",fontWeight:600 }}>{settings.phone}</div>}
    </div>
  );

  const openTasks = comms.filter(c => c.type==="portal" && c.isTask && !c.taskDone && c.direction==="inbound");

  return (
    <div style={{ minHeight:"100vh", background:"#faf9f7", fontFamily:"DM Sans,sans-serif" }}>
      {/* Header */}
      <div style={{ background:"#1a0a0a", padding:"0 20px" }}>
        <div style={{ maxWidth:720, margin:"0 auto", display:"flex",alignItems:"center",justifyContent:"space-between",height:56 }}>
          <div style={{ display:"flex",alignItems:"center",gap:12 }}>
            {settings?.logoBase64
              ? <img src={settings.logoBase64} style={{ height:32,width:"auto",objectFit:"contain" }} alt="logo"/>
              : <div style={{ width:32,height:32,background:"#b91c1c",borderRadius:6,display:"flex",alignItems:"center",justifyContent:"center",color:"white",fontWeight:800,fontSize:12 }}>{(settings?.companyName||"TAF").split(" ").slice(0,2).map(w=>w[0]).join("")}</div>
            }
            <div style={{ color:"white",fontWeight:700,fontSize:14 }}>{settings?.companyName||"TAF"}</div>
          </div>
          <div style={{ display:"flex",gap:4 }}>
            {["fr","en"].map(l=>(
              <button key={l} onClick={()=>setLang(l)} style={{ padding:"4px 10px",background:lang===l?"#b91c1c":"transparent",color:lang===l?"white":"rgba(255,255,255,.5)",border:"none",borderRadius:6,cursor:"pointer",fontSize:12,fontWeight:700,fontFamily:"inherit" }}>
                {l.toUpperCase()}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div style={{ maxWidth:720, margin:"0 auto", padding:"20px 16px" }}>

        {/* Welcome card */}
        <div style={{ background:"#fff",borderRadius:12,border:"1px solid #e5e3df",padding:"16px 20px",marginBottom:20,display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:10 }}>
          <div>
            <div style={{ fontSize:11,color:"#a8a29e",textTransform:"uppercase",letterSpacing:".08em",marginBottom:2 }}>{t.hello}</div>
            <div style={{ fontSize:20,fontWeight:700,color:"#1c1917" }}>{customer.firstName} {customer.lastName}</div>
            {customer.company && <div style={{ fontSize:13,color:"#57534e" }}>{customer.company}</div>}
          </div>
          {openTasks.length > 0 && (
            <div style={{ background:"#fef2f2",border:"1px solid #fecaca",borderRadius:8,padding:"8px 14px",fontSize:12,color:"#dc2626",fontWeight:600 }}>
              {openTasks.length} révision{openTasks.length>1?"s":""} en attente
            </div>
          )}
        </div>

        {/* Tabs */}
        {selectedEstimate ? (
          <EstimateDetail
            estimate={selectedEstimate}
            customer={customer}
            settings={settings}
            t={t}
            isInvoice={selectedType==="invoices"}
            onBack={()=>setSelectedId(null)}
            onApprove={handleApprove}
            onApproveCO={handleApproveCO}
            onBulkApproveCOs={handleBulkApproveCOs}
          />
        ) : (
          <>
            <div style={{ display:"flex",gap:2,marginBottom:16,background:"#fff",borderRadius:10,border:"1px solid #e5e3df",padding:4 }}>
              {[
                ["estimates", t.tabs.estimates, estimates.length],
                ["invoices", t.tabs.invoices, invoices.length],
                ["messages", t.tabs.messages, comms.filter(c=>c.type==="portal").length],
              ].map(([k,l,count])=>(
                <button key={k} onClick={()=>setTab(k)} style={{
                  flex:1,padding:"8px 12px",background:tab===k?"#1a0a0a":"transparent",
                  color:tab===k?"white":"#57534e",border:"none",borderRadius:7,
                  cursor:"pointer",fontSize:13,fontWeight:600,fontFamily:"inherit",
                  display:"flex",alignItems:"center",justifyContent:"center",gap:6,
                }}>
                  {l}
                  {count>0&&<span style={{ background:tab===k?"rgba(255,255,255,.2)":"#e5e3df",color:tab===k?"white":"#57534e",borderRadius:10,padding:"1px 7px",fontSize:10,fontWeight:700 }}>{count}</span>}
                </button>
              ))}
            </div>

            {/* Estimates tab */}
            {tab==="estimates" && (
              <div>
                {estimates.length===0 && (
                  <div style={{ textAlign:"center",color:"#a8a29e",fontSize:13,padding:"40px 0" }}>Aucune soumission pour l'instant.</div>
                )}
                {estimates.length>0 && (
                  <div style={{ fontSize:12,color:"#a8a29e",fontStyle:"italic",marginBottom:12 }}>ℹ️ {t.hints.estimates}</div>
                )}
                {estimates.map(e => {
                  const subtotal = (e.sections||[]).reduce((a,s)=>(s.items||[]).reduce((b,i)=>b+(i.unitPrice||0)*(i.qty||1),a),0);
                  const total = subtotal;
                  return (
                    <div key={e.id} onClick={()=>{setSelectedId(e.id);setSelectedType("estimates");}} style={{ background:"#fff",borderRadius:12,border:"1px solid #e5e3df",padding:"16px 20px",marginBottom:10,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"space-between",gap:12 }}>
                      <div style={{ flex:1 }}>
                        <div style={{ fontWeight:700,fontSize:14,color:"#1c1917",marginBottom:4 }}>{e.title}</div>
                        <div style={{ display:"flex",gap:10,alignItems:"center",flexWrap:"wrap" }}>
                          <span style={{ fontSize:11,color:"#a8a29e" }}>{e.number}</span>
                          {e.validUntil&&<span style={{ fontSize:11,color:"#a8a29e" }}>{t.estimate.validUntil}: {fmtDate(e.validUntil)}</span>}
                          <StatusBadge status={e.status?.toLowerCase()} t={t}/>
                        </div>
                      </div>
                      <div style={{ textAlign:"right",flexShrink:0 }}>
                        <div style={{ fontSize:16,fontWeight:700,color:"#1c1917" }}>{fmt(total)}</div>
                        <div style={{ fontSize:11,color:"#a8a29e" }}>{t.estimate.total}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Invoices tab */}
            {tab==="invoices" && (
              <div>
                {invoices.length===0 && (
                  <div style={{ textAlign:"center",color:"#a8a29e",fontSize:13,padding:"40px 0" }}>Aucune facture pour l'instant.</div>
                )}
                {invoices.length>0 && (
                  <div style={{ fontSize:12,color:"#a8a29e",fontStyle:"italic",marginBottom:12 }}>ℹ️ {t.hints.invoices}</div>
                )}
                {invoices.map(inv => {
                  const invSecSub = (inv.sections||[]).reduce((a,s)=>{const p=s.price!=null?s.price:(s.items||[]).reduce((b,i)=>b+(i.unitPrice||0)*(i.qty||1),0);return a+p;},0);
                  const invCoAmt = (inv.changeOrders||[]).filter(co=>co.status==="approved"||co.status==="manual").reduce((a,co)=>a+(co.amount||0),0);
                  const subtotal = invSecSub + invCoAmt;
                  const totalWithTax = subtotal+Math.round(subtotal*0.05*100)/100+Math.round(subtotal*0.09975*100)/100;
                  return (
                    <div key={inv.id} onClick={()=>{setSelectedId(inv.id);setSelectedType("invoices");}} style={{ background:"#fff",borderRadius:12,border:"1px solid #e5e3df",padding:"16px 20px",marginBottom:10,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"space-between",gap:12 }}>
                      <div style={{ flex:1 }}>
                        <div style={{ fontWeight:700,fontSize:14,color:"#1c1917",marginBottom:4 }}>{inv.title}</div>
                        <div style={{ display:"flex",gap:10,alignItems:"center" }}>
                          <span style={{ fontSize:11,color:"#a8a29e" }}>{inv.number}</span>
                          <StatusBadge status={inv.status?.toLowerCase()||"sent"} t={t}/>
                        </div>
                      </div>
                      <div style={{ textAlign:"right",flexShrink:0 }}>
                        <div style={{ fontSize:16,fontWeight:700,color:"#1c1917" }}>{fmt(totalWithTax)}</div>
                        <div style={{ fontSize:11,color:"#a8a29e" }}>{t.estimate.totalTax}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Messages tab */}
            {tab==="messages" && (
              <MessagesTab comms={comms} customerId={customer.id} t={t} onSend={handleSendMessage}/>
            )}
          </>
        )}

        {/* Footer */}
        <div style={{ textAlign:"center",marginTop:32,paddingTop:16,borderTop:"1px solid #e5e3df",fontSize:11,color:"#a8a29e" }}>
          {settings?.companyName} {settings?.phone&&`· ${settings.phone}`} {settings?.email&&`· ${settings.email}`}
        </div>
      </div>
    </div>
  );
}
